import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, BarChart3, DollarSign, Package, Users, Cpu } from 'lucide-react';
import AlertsPanel from './AlertsPanel';

const API_URL = '';

const TREND_ICON = {
  up: { Icon: TrendingUp, cls: 'text-green-600', bg: 'bg-green-50' },
  down: { Icon: TrendingDown, cls: 'text-red-600', bg: 'bg-red-50' },
  flat: { Icon: Minus, cls: 'text-muted-foreground', bg: 'bg-muted' },
};

function KpiCard({ metric }) {
  const t = TREND_ICON[metric.trend] || TREND_ICON.flat;
  const change = metric.change;
  const changeStr = change > 0 ? `+${change}` : change === 0 ? '—' : `${change}`;
  const hasData = metric.this_week > 0 || metric.last_week > 0;

  return (
    <div className="bg-white rounded-xl p-4 border border-border shadow-sm" data-testid={`kpi-${metric.label.toLowerCase().replace(/\s/g, '-')}`}>
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1">{metric.label}</p>
      {hasData ? (
        <>
          <p className="text-2xl font-medium text-foreground" style={{ fontFamily: 'Outfit' }}>
            {metric.this_week_display}
          </p>
          <div className="flex items-center gap-1.5 mt-1.5">
            <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${t.bg} ${t.cls}`}>
              <t.Icon className="w-3 h-3" />
              {changeStr}
            </div>
            <span className="text-[10px] text-muted-foreground">vs last week ({metric.last_week_display})</span>
          </div>
        </>
      ) : (
        <>
          <p className="text-2xl font-medium text-muted-foreground/40" style={{ fontFamily: 'Outfit' }}>—</p>
          <p className="text-[10px] text-muted-foreground mt-1.5">No data this week</p>
        </>
      )}
    </div>
  );
}

function WeeklyKpiDashboard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API_URL}/api/admin/kpi/weekly`, {
      headers: { Cookie: `access_token=${token}` },
      credentials: 'include',
    })
      .then(r => r.json())
      .then(d => { setData(d.kpi); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) return <p className="text-sm text-muted-foreground py-4 text-center">Loading KPIs...</p>;
  if (!data) return <p className="text-sm text-muted-foreground py-4 text-center">Unable to load KPIs.</p>;

  const metrics = data.metrics || [];
  const weekDate = new Date(data.week_start).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
  const hasAnyData = metrics.some(m => m.this_week > 0 || m.last_week > 0);

  return (
    <div data-testid="weekly-kpi-dashboard">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold">Weekly KPIs — Powersure NL</h3>
          <p className="text-[10px] text-muted-foreground">Week starting {weekDate}</p>
        </div>
        {!hasAnyData && (
          <span className="text-[10px] text-amber-600 bg-amber-50 px-2 py-1 rounded-full">Waiting for live traffic data</span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {metrics.map(m => <KpiCard key={m.label} metric={m} />)}
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white rounded-xl p-5 border border-border shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 ${accent || 'text-primary'}`} />
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
    </div>
    <p className="text-2xl font-light text-foreground" style={{ fontFamily: 'Outfit' }}>{value}</p>
  </div>
);

export default function OverviewTab({ stats, reports, transactions, token }) {
  if (!stats) return null;

  const recentReports = reports.slice(0, 5);
  const recentTxns = transactions.filter(t => t.payment_status === 'paid').slice(0, 5);

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {/* KPI Alert Bot - top priority */}
      <AlertsPanel token={token} />

      {/* Weekly KPIs - primary focus */}
      <WeeklyKpiDashboard token={token} />

      {/* Platform totals */}
      <div>
        <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Platform Totals</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard icon={BarChart3} label="Reports" value={stats.total_reports} />
          <StatCard icon={DollarSign} label="Paid" value={stats.total_payments} accent="text-green-600" />
          <StatCard icon={Package} label="Products" value={`${stats.approved_products} / ${stats.pending_products + stats.approved_products}`} />
          <StatCard icon={Users} label="Installers" value={`${stats.approved_installers} / ${stats.pending_installers + stats.approved_installers}`} />
          <StatCard icon={Cpu} label="Running Tasks" value={stats.running_tasks} accent="text-amber-600" />
        </div>
      </div>

      {stats.flagged_products > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <Package className="w-5 h-5 text-amber-600" />
          <p className="text-sm text-amber-800 font-medium">{stats.flagged_products} product(s) flagged for review</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-5 border border-border shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Recent Reports</h3>
          {recentReports.length === 0 ? (
            <p className="text-sm text-muted-foreground">No reports yet</p>
          ) : (
            <div className="space-y-2">
              {recentReports.map(r => (
                <div key={r.report_id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div>
                    <p className="text-sm font-medium">{r.home_profile?.home_type} — {r.country}</p>
                    <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.unlocked ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>
                    {r.unlocked ? 'Unlocked' : 'Locked'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl p-5 border border-border shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Recent Payments</h3>
          {recentTxns.length === 0 ? (
            <p className="text-sm text-muted-foreground">No payments yet</p>
          ) : (
            <div className="space-y-2">
              {recentTxns.map(t => (
                <div key={t.session_id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div>
                    <p className="text-sm font-medium">{t.plan_name} — €{t.amount}</p>
                    <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Paid</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
